import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import DashboardCard from './DashboardCard';
import { formatTry, PROJECT_OTHER_COLOR, PROJECT_TOP_N, projectLabel } from '../constants';
import { LegendItem } from '../styles';

// Proje harcama trendi — son 6 ay, en çok harcayan 5 proje + "diğer projeler" yığılmış alan.
// Projesiz harcama bu grafikte YOK (payı halka kartında) — trend yalnız projelerin seyrini
// anlatır.

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-top: 8px;
`;

const Swatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 6px;
  background: ${({ $color }) => $color};
`;

/// { rows: [{ label, [seriesKey]: tutar }], series: [{ key, name, color }] } (saf).
export function buildTrendModel(data, projectColors, t, lang) {
  if (!data) return { rows: [], series: [] };
  const months = data.months || [];
  const projeler = (data.items || [])
    .filter((i) => i.projectId && i.windowTotal > 0)
    .sort((a, b) => b.windowTotal - a.windowTotal);
  const top = projeler.slice(0, PROJECT_TOP_N);
  const topIds = new Set(top.map((p) => p.projectId));
  const digerVar = projeler.length > top.length;

  const series = top.map((p) => ({ key: p.projectId, name: projectLabel(p, t), color: projectColors.get(p.projectId) || PROJECT_OTHER_COLOR }));
  if (digerVar) series.push({ key: 'other', name: t('expenseDashboard.projects.otherProjects'), color: PROJECT_OTHER_COLOR });

  const ayAnahtari = (iso) => String(iso).slice(0, 7);
  const rows = months.map((m) => {
    const [y, mo] = ayAnahtari(m).split('-').map(Number);
    const row = { month: ayAnahtari(m), label: new Date(y, mo - 1, 1).toLocaleDateString(lang, { month: 'short' }) };
    series.forEach((s) => { row[s.key] = 0; });
    return row;
  });
  const byMonth = new Map(rows.map((r) => [r.month, r]));

  (data.monthlyMatrix || []).forEach((c) => {
    if (!c.projectId) return;
    const row = byMonth.get(ayAnahtari(c.monthStart));
    if (!row) return;
    const key = topIds.has(c.projectId) ? c.projectId : (digerVar ? 'other' : null);
    if (key) row[key] += Math.round(c.total);
  });

  return { rows, series };
}

export default function ProjectTrendCard({ slice, projectColors }) {
  const { t, i18n } = useTranslation();
  const data = slice.data;

  const { rows, series } = useMemo(
    () => buildTrendModel(data, projectColors, t, i18n.language),
    [data, projectColors, t, i18n.language],
  );

  return (
    <DashboardCard
      cardKey="proje-trendi"
      title={t('expenseDashboard.projects.trendTitle')}
      meta={t('expenseDashboard.projects.windowMeta')}
      span={8}
      spanMd={6}
      loading={slice.loading}
      empty={series.length === 0}
      emptyText={t('expenseDashboard.projects.empty')}
    >
      <div>
        <ResponsiveContainer width="100%" height={190}>
          <AreaChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: 8 }}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`proje-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={s.color} stopOpacity={0.55} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0.08} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke="#EEF1F5" />
            <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: '#E1E0D9' }} tick={{ fontSize: 10, fill: '#898781' }} />
            <YAxis hide />
            <Tooltip formatter={(value, name) => [formatTry(value), name]} />
            {series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stackId="proje"
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#proje-${s.key})`}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
        <LegendRow>
          {series.map((s) => (
            <LegendItem key={s.key} data-testid={`trend-legend-${s.key}`}>
              <Swatch $color={s.color} />
              {s.name}
            </LegendItem>
          ))}
        </LegendRow>
      </div>
    </DashboardCard>
  );
}
